import { useEffect, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Link } from "react-router-dom";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,300&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
  html { scroll-behavior: smooth; }
`;

/* ───────────────────────── Shell ───────────────────────── */

const Page = styled.div`
  background: #fff;
  min-height: 100vh;
  font-family: "Mendl", sans-serif;
  color: #0a0010;
`;

const Container = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 clamp(1.25rem, 5vw, 3.5rem);
`;

/* ───────────────────────── Hero ───────────────────────── */

const Hero = styled.section`
  padding: clamp(6rem, 12vw, 8.5rem) 0 clamp(2.5rem, 5vw, 3.5rem);
  border-bottom: 1px solid rgba(168, 85, 247, 0.12);
`;

const Eyebrow = styled.div`
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #a855f7;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.6rem;

  &::before {
    content: "";
    width: 32px;
    height: 1px;
    background: #a855f7;
    display: inline-block;
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.4rem, 5.5vw, 4.2rem);
  font-weight: 600;
  line-height: 1.05;
  letter-spacing: -0.01em;
  margin-bottom: 1.2rem;
`;

const HeroMeta = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.85rem;
  font-weight: 400;
  color: rgba(10, 0, 16, 0.5);
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1.5rem;
`;

const HeroLede = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.8;
  color: rgba(10, 0, 16, 0.65);
  max-width: 640px;
  margin-top: 1.6rem;
`;

const AcceptBanner = styled.div`
  margin-top: 1.8rem;
  padding: 1.1rem 1.4rem;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgba(232, 121, 249, 0.08),
    rgba(124, 58, 237, 0.08)
  );
  border: 1px solid rgba(168, 85, 247, 0.18);
  font-family: "DM Sans", sans-serif;
  font-size: 0.82rem;
  font-weight: 500;
  line-height: 1.7;
  color: #4c1d75;
  max-width: 760px;
`;

/* ───────────────────────── Layout ───────────────────────── */

const Layout = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: clamp(2rem, 4vw, 5rem);
  padding: clamp(3rem, 6vw, 4.5rem) 0 6rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const TocWrap = styled.nav`
  position: sticky;
  top: clamp(1.5rem, 4vh, 2.5rem);
  max-height: calc(100vh - 3rem);
  overflow-y: auto;
  padding-right: 0.5rem;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(168, 85, 247, 0.25);
    border-radius: 4px;
  }

  @media (max-width: 900px) {
    position: static;
    max-height: none;
    margin-bottom: 1rem;
  }
`;

const TocTitle = styled.div`
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(10, 0, 16, 0.35);
  margin-bottom: 1rem;
`;

const TocList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const TocLink = styled.a`
  font-family: "DM Sans", sans-serif;
  font-size: 0.83rem;
  font-weight: 400;
  color: ${({ $active }) => ($active ? "#7c3aed" : "rgba(10, 0, 16, 0.55)")};
  text-decoration: none;
  display: block;
  line-height: 1.4;
  border-left: 2px solid
    ${({ $active }) => ($active ? "#a855f7" : "transparent")};
  padding-left: 0.75rem;
  transition:
    color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    color: #7c3aed;
  }
`;

const Content = styled.div`
  max-width: 760px;
`;

const Section = styled.section`
  scroll-margin-top: 2rem;
  padding-bottom: clamp(2.2rem, 4vw, 3rem);
  margin-bottom: clamp(2.2rem, 4vw, 3rem);
  border-bottom: 1px solid rgba(168, 85, 247, 0.1);

  &:last-of-type {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.3rem, 2.2vw, 1.6rem);
  font-weight: 600;
  margin-bottom: 1.1rem;
  display: flex;
  align-items: baseline;
  gap: 0.6rem;

  span {
    font-size: 0.75rem;
    font-weight: 500;
    color: #a855f7;
    letter-spacing: 0.05em;
  }
`;

const SubTitle = styled.h3`
  font-size: 1.02rem;
  font-weight: 600;
  margin: 1.6rem 0 0.7rem;
  color: #1a0b2e;
`;

const P = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 0.94rem;
  font-weight: 300;
  line-height: 1.85;
  color: rgba(10, 0, 16, 0.68);
  margin-bottom: 0.9rem;

  strong {
    font-weight: 600;
    color: #0a0010;
  }
`;

const List = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin: 0.6rem 0 1rem;
`;

const ListItem = styled.li`
  font-family: "DM Sans", sans-serif;
  font-size: 0.94rem;
  font-weight: 300;
  line-height: 1.75;
  color: rgba(10, 0, 16, 0.68);
  padding-left: 1.2rem;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0.55em;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #a855f7;
  }

  strong {
    font-weight: 600;
    color: #0a0010;
  }
`;

const Callout = styled.div`
  background: rgba(168, 85, 247, 0.05);
  border: 1px solid rgba(168, 85, 247, 0.15);
  border-radius: 10px;
  padding: 1rem 1.2rem;
  margin: 0.9rem 0;

  p {
    margin-bottom: 0;
  }
`;

/* Zero-tolerance / permanent-termination callouts get a distinct, more
   urgent treatment so they read as categorically different from the
   ordinary purple-accented content around them. */
const CriticalCallout = styled.div`
  background: rgba(220, 38, 38, 0.045);
  border: 1px solid rgba(220, 38, 38, 0.22);
  border-left: 3px solid #dc2626;
  border-radius: 8px;
  padding: 1rem 1.2rem;
  margin: 0.9rem 0;

  p {
    margin-bottom: 0;
    color: rgba(10, 0, 16, 0.75);
  }

  strong {
    color: #b91c1c;
  }
`;

const CriticalLabel = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #dc2626;
  margin-bottom: 0.5rem;
`;

const DefTerm = styled.div`
  margin-bottom: 1.1rem;

  dt {
    font-family: "DM Sans", sans-serif;
    font-weight: 600;
    font-size: 0.94rem;
    color: #0a0010;
    margin-bottom: 0.3rem;
  }

  dd {
    font-family: "DM Sans", sans-serif;
    font-weight: 300;
    font-size: 0.94rem;
    line-height: 1.8;
    color: rgba(10, 0, 16, 0.68);
    margin-left: 0;
  }
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0.8rem 0 1rem;
`;

const ContactRow = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.94rem;
  color: rgba(10, 0, 16, 0.68);

  b {
    color: #0a0010;
    font-weight: 600;
  }

  a {
    color: #7c3aed;
    text-decoration: none;
    font-weight: 500;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Todo = styled.span`
  color: #b45309;
  border-bottom: 1px dotted #d97706;
  font-style: italic;
`;

const BackHome = styled(Link)`
  font-family: "DM Sans", sans-serif;
  font-size: 0.85rem;
  color: #7c3aed;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 2rem;

  &:hover {
    text-decoration: underline;
  }
`;

/* ───────────────────────── Data ───────────────────────── */

const TOC = [
  { id: "purpose", label: "1. Purpose, Scope & Incorporation" },
  { id: "definitions", label: "2. Definitions" },
  { id: "interpretation", label: "3. Interpretation" },
  { id: "eligibility", label: "4. Eligibility: Adults Only" },
  { id: "content-standards", label: "5. Content Standards" },
  { id: "minors", label: "6. Zero Tolerance: Minors" },
  { id: "consent", label: "7. Consent & Interaction" },
  { id: "harassment", label: "8. Harassment, Hate & Abuse" },
  { id: "sexual-violence", label: "9. Sexual Violence & Exploitation" },
  { id: "nonconsensual", label: "10. Non-Consensual Intimate Content" },
  { id: "fraud", label: "11. Fraud & Illegal Conduct" },
  { id: "enforcement", label: "12. Moderation & Enforcement" },
  { id: "appeals", label: "13. Reporting & Appeals" },
  { id: "general", label: "14. General Provisions" },
  { id: "contact", label: "15. Contact" },
];

export default function GuidelinesPage() {
  const [active, setActive] = useState(TOC[0].id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-15% 0px -70% 0px" },
    );
    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <Page>
      <GlobalStyle />

      <Hero>
        <Container>
          <Eyebrow>Legal</Eyebrow>
          <HeroTitle>Acceptable Use Policy</HeroTitle>
          <HeroMeta>
            <span>
              Effective Date: <Todo>July 9, 2026</Todo>
            </span>
            <span>
              Last Updated: <Todo>July 9, 2026</Todo>
            </span>
          </HeroMeta>
          <HeroLede>
            NudR is built for bold connections — confidence, chemistry, and
            honesty about attraction. Being bold does not mean being unsafe, and
            it never means being explicit. This Acceptable Use Policy — also
            referred to throughout our documentation as our Community
            Guidelines (the "Guidelines") — defines the content and conduct
            standards applicable to the Service.
          </HeroLede>
          <AcceptBanner>
            These Guidelines are incorporated into, and form an integral part
            of, the{" "}
            <Link to="/terms" style={{ color: "#4c1d75" }}>
              NudR Terms of Service
            </Link>
            . Any violation of these Guidelines constitutes a material breach of
            the Terms. Where the two conflict, the Terms control.
          </AcceptBanner>
        </Container>
      </Hero>

      <Container>
        <Layout>
          <TocWrap>
            <TocTitle>On this page</TocTitle>
            <TocList>
              {TOC.map((item) => (
                <li key={item.id}>
                  <TocLink href={`#${item.id}`} $active={active === item.id}>
                    {item.label}
                  </TocLink>
                </li>
              ))}
            </TocList>
          </TocWrap>

          <Content>
            {/* 1. Purpose */}
            <Section id="purpose">
              <SectionTitle>
                <span>01</span>Purpose, Scope, and Incorporation
              </SectionTitle>

              <SubTitle>1.1 Incorporation into the Terms of Service</SubTitle>
              <P>
                These Guidelines are incorporated into, and form an integral
                part of, the NudR Terms of Service (the "Terms"). Capitalized
                terms not defined in these Guidelines have the meanings given to
                them in the Terms. Any violation of these Guidelines constitutes
                a material breach of the Terms. In the event of any conflict or
                inconsistency between these Guidelines and the Terms, the Terms
                control.
              </P>

              <SubTitle>1.2 Scope</SubTitle>
              <P>
                These Guidelines apply to all use of the Service and to all
                content and conduct on or connected to the Service, including:
                (a) profile information, photographs (whether set to public or
                hidden visibility), bios, prompts, and photo tags; (b) messages,
                images, voice notes, video, and calls exchanged through the
                Service, including consent-based Shared Media; (c) reports,
                appeals, and communications with NudR; and (d) conduct that
                begins on the Service and continues off the Service, or
                off-Service conduct directed at a person the user met through
                the Service, where such conduct threatens the safety or
                integrity of the Service or its users.
              </P>

              <SubTitle>1.3 Ongoing Obligation</SubTitle>
              <P>
                Compliance with these Guidelines is a continuing condition of
                access to the Service. Users are responsible for reviewing the
                then-current version of these Guidelines, which is available
                within the Service and at nudr.app.
              </P>

              <SubTitle>1.4 Related Policies</SubTitle>
              <P>
                These Guidelines should be read alongside the following, each of
                which forms part of NudR's policy framework:
              </P>
              <List>
                <ListItem>
                  <Link to="/terms" style={{ color: "#7c3aed" }}>
                    Terms of Service
                  </Link>{" "}
                  — the agreement governing use of the Service.
                </ListItem>
                <ListItem>
                  <Link to="/child-safety" style={{ color: "#7c3aed" }}>
                    Child Safety Standards
                  </Link>{" "}
                  — our full standards against child sexual abuse and
                  exploitation, and how we report to NCMEC. See also Section 6.
                </ListItem>
                <ListItem>
                  <Link to="/safety-tools" style={{ color: "#7c3aed" }}>
                    Reporting &amp; Blocking
                  </Link>{" "}
                  — how to report a profile, message, or member, and what
                  happens next.
                </ListItem>
                <ListItem>
                  <Link to="/privacy" style={{ color: "#7c3aed" }}>
                    Privacy Policy
                  </Link>{" "}
                  — how we handle your information.
                </ListItem>
                <ListItem>
                  <Link to="/law-enforcement" style={{ color: "#7c3aed" }}>
                    Law Enforcement Guidelines
                  </Link>{" "}
                  — how agencies serve legal process on NudR.
                </ListItem>
              </List>
            </Section>

            {/* 2. Definitions */}
            <Section id="definitions">
              <SectionTitle>
                <span>02</span>Definitions
              </SectionTitle>

              <dl>
                <DefTerm>
                  <dt>"Explicit Content"</dt>
                  <dd>
                    means content that is pornographic or sexually explicit,
                    including any depiction — real, simulated, illustrated,
                    animated, or generated in whole or in part by artificial
                    intelligence — of exposed genitalia, exposed buttocks,
                    exposed female nipples, sexual acts, sexual penetration, or
                    masturbation, as well as content that uses sheer coverings,
                    partial obstruction, emoji, digital alteration, framing, or
                    similar techniques with the purpose or effect of displaying
                    or simulating any of the foregoing.
                  </dd>
                </DefTerm>
                <DefTerm>
                  <dt>"Intimate Content"</dt>
                  <dd>
                    means any content depicting a person in a state of nudity or
                    partial nudity, or engaged in actual or simulated sexual
                    activity, whether or not that content would qualify as
                    Explicit Content.
                  </dd>
                </DefTerm>
                <DefTerm>
                  <dt>"Shared Media"</dt>
                  <dd>
                    means images, voice notes, video, and calls exchanged
                    between matched users through the Service's consent-based
                    media features.
                  </dd>
                </DefTerm>
                <DefTerm>
                  <dt>"Minor"</dt>
                  <dd>
                    means any person under 18 years of age, or under the age of
                    majority in the jurisdiction where they reside, whichever is
                    higher, and includes any person who appears to be, is
                    presented as, or is stated to be under 18, regardless of
                    actual age.
                  </dd>
                </DefTerm>
                <DefTerm>
                  <dt>"Enforcement Action"</dt>
                  <dd>
                    means any action NudR takes in response to an actual,
                    suspected, or attempted violation, including warning,
                    content removal, content blurring or visibility restriction,
                    feature restriction, temporary suspension, permanent
                    termination, device or identity blocking, and referral to
                    law enforcement.
                  </dd>
                </DefTerm>
              </dl>
            </Section>

            {/* 3. Interpretation */}
            <Section id="interpretation">
              <SectionTitle>
                <span>03</span>Interpretation
              </SectionTitle>
              <P>In these Guidelines:</P>
              <List>
                <ListItem>
                  "Including" and "for example" mean "including, without
                  limitation."
                </ListItem>
                <ListItem>
                  All lists and examples are illustrative and not exhaustive —
                  conduct or content of a similar nature, severity, or purpose
                  is prohibited even if not expressly listed.
                </ListItem>
                <ListItem>
                  Headings are for convenience only and do not affect
                  interpretation.
                </ListItem>
                <ListItem>
                  NudR determines, in its sole and reasonable discretion,
                  whether content or conduct violates these Guidelines, and such
                  determinations are effective upon being made.
                </ListItem>
                <ListItem>
                  Attempting, soliciting, encouraging, facilitating, or
                  assisting a violation of these Guidelines is itself a
                  violation, whether or not the underlying violation is
                  completed.
                </ListItem>
              </List>
            </Section>

            {/* 4. Eligibility */}
            <Section id="eligibility">
              <SectionTitle>
                <span>04</span>Eligibility: Adults Only
              </SectionTitle>
              <List>
                <ListItem>
                  You must be at least 18 years of age to access or use the
                  Service. NudR verifies date of birth and operates an
                  identity-verification program as described in the Terms.
                </ListItem>
                <ListItem>
                  Every identifiable person appearing in any content you submit
                  must be 18 years of age or older and must have consented to
                  appear in that content.
                </ListItem>
                <ListItem>
                  Misrepresenting your age, or knowingly interacting with a
                  Minor on the Service, is a material breach resulting in
                  immediate permanent termination.
                </ListItem>
              </List>
            </Section>

            {/* 5. Content Standards */}
            <Section id="content-standards">
              <SectionTitle>
                <span>05</span>Content Standards: Bold, Not Bare
              </SectionTitle>

              <SubTitle>5.1 The Standard</SubTitle>
              <P>
                NudR permits confident, suggestive, and flirtatious content.
                NudR prohibits Explicit Content{" "}
                <strong>in every part of the Service, without exception</strong>{" "}
                — including profile photographs (public or hidden), bios,
                prompts, photo tags, and Shared Media exchanged privately
                between matched users. Mutual consent between users does not
                authorize Explicit Content; the consent-based media features of
                the Service govern <em>whether</em> media may be exchanged, not{" "}
                <em>what</em> that media may depict.
              </P>

              <SubTitle>5.2 Permitted Content</SubTitle>
              <P>
                Subject to the prohibitions in these Guidelines, the following
                is permitted:
              </P>
              <List>
                <ListItem>
                  Swimwear, lingerie, shirtless, athletic, and beach
                  photography.
                </ListItem>
                <ListItem>
                  Suggestive poses, flirtatious framing, and implied sensuality,
                  provided no Explicit Content is visible or simulated.
                </ListItem>
                <ListItem>
                  Confident, flirtatious conversation between matched adults,
                  subject to the consent and interaction standards in Section 7.
                </ListItem>
              </List>

              <SubTitle>5.3 Prohibited Content</SubTitle>
              <P>
                You may not post, upload, transmit, request, or solicit, in any
                part of the Service:
              </P>
              <List>
                <ListItem>
                  Explicit Content of any kind, as defined in Section 2.
                </ListItem>
                <ListItem>
                  Links, references, codes, or invitations directing users to
                  Explicit Content or to platforms for exchanging it, whether on
                  or off the Service.
                </ListItem>
                <ListItem>
                  Sexually explicit text presented as profile content, including
                  in bios, prompts, usernames, and photo tags.
                </ListItem>
                <ListItem>
                  Content that sexualizes bodily fluids, violence, coercion,
                  incapacitation, or non-consent, whether or not it otherwise
                  qualifies as Explicit Content.
                </ListItem>
              </List>

              <SubTitle>5.4 Close Calls</SubTitle>
              <P>
                If content requires debate about whether it crosses the line,
                NudR may treat it as crossing the line. Users are expected to
                exercise judgment; the availability of an appeal under Section
                12 does not entitle any user to test the boundary repeatedly.
              </P>
            </Section>

            {/* 6. Minors */}
            <Section id="minors">
              <SectionTitle>
                <span>06</span>Zero Tolerance: Content Involving Minors
              </SectionTitle>
              <CriticalCallout>
                <CriticalLabel>Zero tolerance · No appeal</CriticalLabel>
                <P>
                  Any content that depicts, sexualizes, or exploits a Minor —
                  whether real, apparent, illustrated, animated, or
                  AI-generated, and regardless of the poster's intent or the
                  apparent consent of any person depicted — results in{" "}
                  <strong>
                    immediate permanent termination without warning
                  </strong>
                  , preservation of relevant evidence, and reporting to the
                  National Center for Missing & Exploited Children (NCMEC)
                  and/or law enforcement as required by applicable law,
                  including 18 U.S.C. § 2258A. This category includes grooming
                  behavior, attempts to identify or contact Minors through the
                  Service, and requests for content involving Minors.{" "}
                  <strong>
                    No appeal is available for Enforcement Actions under this
                    Section.
                  </strong>
                </P>
              </CriticalCallout>
              <P>
                Our full child-safety standards — including how we detect
                violations, how we report child sexual abuse material to the
                NCMEC CyberTipline, how to report a concern to us, and external
                reporting resources — are set out in our{" "}
                <Link to="/child-safety" style={{ color: "#7c3aed" }}>
                  Child Safety Standards
                </Link>
                .
              </P>
            </Section>

            {/* 7. Consent */}
            <Section id="consent">
              <SectionTitle>
                <span>07</span>Consent and Interaction Standards
              </SectionTitle>
              <P>
                Boldness requires consent. The following standards apply to all
                interactions on the Service:
              </P>
              <List>
                <ListItem>
                  <strong>Consent is per-type, mutual, and revocable.</strong>{" "}
                  Exchanging Shared Media requires mutual, per-type consent
                  granted within the conversation. Either user may revoke
                  consent at any time; revocation must be respected immediately
                  and applies prospectively.
                </ListItem>
                <ListItem>
                  <strong>No pressure.</strong> Pressuring, badgering,
                  guilt-tripping, deceiving, or otherwise manipulating a user
                  into granting consent, continuing a conversation, or sharing
                  content is prohibited.
                </ListItem>
                <ListItem>
                  <strong>No unsolicited sexual conduct.</strong> Unsolicited
                  sexual commentary, degrading remarks, and continuing sexual
                  conversation after another user has declined, objected, or
                  disengaged constitute harassment under Section 8.
                </ListItem>
                <ListItem>
                  <strong>Disengagement is final.</strong> A "no," an unmatch, a
                  block, or sustained silence is a complete answer. Attempting
                  to contact, locate, or monitor a user who has unmatched or
                  blocked you — through new accounts, other users, other
                  platforms, or in person — is a serious violation and may
                  constitute stalking or harassment under applicable law.
                </ListItem>
              </List>
            </Section>

            {/* 8. Harassment */}
            <Section id="harassment">
              <SectionTitle>
                <span>08</span>Harassment, Hate, and Abusive Conduct
              </SectionTitle>
              <P>The following conduct is prohibited:</P>
              <List>
                <ListItem>
                  Harassment, threats, stalking, intimidation, bullying, or
                  coordinated abuse targeting any person.
                </ListItem>
                <ListItem>
                  Hate speech, slurs, or discriminatory conduct targeting race,
                  color, caste, ethnicity, national origin, religion, sex,
                  gender, gender identity or expression, sexual orientation,
                  disability, serious disease, immigration status, or veteran
                  status.
                </ListItem>
                <ListItem>
                  Body-shaming, kink-shaming, or ridiculing another user's
                  appearance, preferences, boundaries, or refusal.
                </ListItem>
                <ListItem>
                  Doxxing: publishing, sharing, or threatening to share another
                  person's private, identifying, or contact information without
                  that person's consent.
                </ListItem>
                <ListItem>
                  Credible threats of violence, incitement to violence, or
                  glorification of violence against any person or group.
                </ListItem>
              </List>
            </Section>

            {/* 9. Non-Consensual */}
            {/* 9. Sexual violence */}
            <Section id="sexual-violence">
              <SectionTitle>
                <span>09</span>Sexual Violence and Sexual Exploitation
              </SectionTitle>
              <CriticalCallout>
                <CriticalLabel>Zero tolerance</CriticalLabel>
                <P>
                  <strong>
                    Sexual violence is strictly prohibited on NudR, in every
                    form, without exception.
                  </strong>{" "}
                  This applies to conduct occurring on the Service, to conduct
                  arranged or facilitated through the Service, and to conduct
                  occurring off the Service between people who met through it.
                </P>
              </CriticalCallout>

              <SubTitle>9.1 Prohibited Conduct</SubTitle>
              <P>
                You may not use NudR to commit, attempt, arrange, threaten,
                promote, glorify, normalize, or facilitate any act of sexual
                violence, including:
              </P>
              <List>
                <ListItem>
                  <strong>Rape and sexual assault</strong> — any sexual act
                  committed without the freely given, informed, ongoing consent
                  of every person involved.
                </ListItem>
                <ListItem>
                  <strong>Coerced or pressured sexual activity</strong> —
                  obtaining sexual contact through threats, intimidation,
                  blackmail, abuse of authority, deception as to the nature of
                  the act, or persistent pressure after a refusal.
                </ListItem>
                <ListItem>
                  <strong>
                    Sexual activity with a person incapable of consenting
                  </strong>{" "}
                  — including where a person is asleep, unconscious, or
                  incapacitated by alcohol or drugs, or otherwise unable to
                  understand or refuse the act.
                </ListItem>
                <ListItem>
                  <strong>Drug-facilitated sexual assault</strong> —
                  administering, or attempting to administer, any substance to
                  a person to impair their capacity to consent, and any
                  discussion of doing so.
                </ListItem>
                <ListItem>
                  <strong>Stealthing and consent violations during sex</strong>{" "}
                  — including removing or damaging a condom without the other
                  person's knowledge or agreement, or continuing a sexual act
                  after consent has been withdrawn.
                </ListItem>
                <ListItem>
                  <strong>Sexual exploitation</strong> — taking sexual
                  advantage of another person's vulnerability, dependency,
                  incapacity, immigration status, financial hardship, or
                  situation of control.
                </ListItem>
                <ListItem>
                  <strong>Image-based sexual abuse</strong> — recording,
                  distributing, or threatening to distribute sexual images of a
                  person without their consent. See also Section 10.
                </ListItem>
                <ListItem>
                  <strong>Sexual harassment</strong> — unwanted sexual
                  advances, requests, or commentary directed at a person who has
                  declined, objected, or disengaged.
                </ListItem>
                <ListItem>
                  <strong>Content depicting or endorsing sexual violence</strong>{" "}
                  — including material that presents non-consent, coercion, or
                  incapacitation as desirable, and any expression of intent to
                  commit such acts.
                </ListItem>
              </List>

              <SubTitle>9.2 Aiding, Abetting, and Facilitation</SubTitle>
              <P>
                It is equally a violation to{" "}
                <strong>
                  aid, abet, encourage, solicit, conspire in, fund, instruct on,
                  or otherwise facilitate
                </strong>{" "}
                any act of sexual violence, sexual exploitation, or abuse of any
                person — whether or not you carry out the act yourself, and
                whether or not the act is completed. This includes providing the
                means, opportunity, guidance, or encouragement for another
                person to do so, and includes conduct directed at a person who
                is not a NudR member.
              </P>

              <SubTitle>9.3 Consent</SubTitle>
              <P>
                For the purposes of these Guidelines, consent must be{" "}
                <strong>
                  freely given, informed, specific, ongoing, and capable of
                  being withdrawn at any time
                </strong>
                . Consent to one act is not consent to another. Consent given
                previously is not consent on a later occasion. Silence, passivity,
                intoxication, or the absence of refusal do not constitute
                consent. A person who is asleep, unconscious, incapacitated, or
                otherwise unable to understand the act cannot consent.
              </P>

              <SubTitle>9.4 Enforcement and Reporting</SubTitle>
              <P>
                Violations of this Section bypass the enforcement ladder and
                result in{" "}
                <strong>immediate permanent termination</strong>, preservation of
                evidence, and referral to law enforcement where appropriate. Ban
                evasion controls are applied to prevent re-registration.
              </P>
              <P>
                If you have experienced or witnessed sexual violence connected to
                NudR, report it in the app or contact{" "}
                <a href="mailto:security@nudr.app" style={{ color: "#7c3aed" }}>
                  security@nudr.app
                </a>
                . You do not need a NudR account to report to us.{" "}
                <strong>
                  If you are in immediate danger, contact your local emergency
                  services first
                </strong>{" "}
                — in the United States, call 911. Confidential support is
                available from RAINN at 1-800-656-4673, 24 hours a day.
              </P>
            </Section>

            <Section id="nonconsensual">
              <SectionTitle>
                <span>10</span>Non-Consensual Intimate Content; Privacy of
                Shared Media
              </SectionTitle>

              <SubTitle>10.1 Non-Consensual Intimate Content</SubTitle>
              <CriticalCallout>
                <CriticalLabel>Permanent termination</CriticalLabel>
                <P>
                  Sharing, publishing, threatening to share (including for
                  money, content, or compliance — "sextortion"), or soliciting
                  Intimate Content of any person without that person's consent
                  is prohibited,{" "}
                  <strong>
                    whether the content originated on or off the Service
                  </strong>
                  . This is a permanent-termination offense, may be preserved as
                  evidence, and may be reported to law enforcement. Federal and
                  state law, including laws addressing non-consensual intimate
                  imagery, may impose civil and criminal liability for such
                  conduct.
                </P>
              </CriticalCallout>

              <SubTitle>10.2 Screenshot and Capture Protections</SubTitle>
              <List>
                <ListItem>
                  Shared Media is protected by operating-system-level screenshot
                  and screen-recording restrictions. Where the device or
                  operating system does not permit blocking, capture attempts
                  are detected and the other participant is notified, as
                  described in the Terms.
                </ListItem>
                <ListItem>
                  Detected capture attempts accrue strikes against your account.
                  Accumulated strikes result in escalating Enforcement Action,
                  up to permanent termination.
                </ListItem>
                <ListItem>
                  Circumventing or evading capture protections by any means —
                  including third-party software, rooted or jailbroken devices,
                  emulators, or photographing the screen with a separate device
                  — is a violation regardless of whether the circumvention is
                  technically detected.
                </ListItem>
                <ListItem>
                  Distributing, publishing, or threatening to distribute
                  captured Shared Media is treated as non-consensual intimate
                  content under Section 10.1.
                </ListItem>
              </List>
            </Section>

            {/* 10. Fraud */}
            <Section id="fraud">
              <SectionTitle>
                <span>11</span>Fraud, Commercial Activity, and Illegal Conduct
              </SectionTitle>
              <List>
                <ListItem>
                  <strong>No financial solicitation.</strong> You may not
                  request money, cryptocurrency, gift cards, financial account
                  details, investment participation, or anything of value from
                  another user, or engage in romance-scam or confidence-fraud
                  conduct. This results in immediate permanent termination and
                  referral to law enforcement.
                </ListItem>
                <ListItem>
                  <strong>No commercial sexual services.</strong> Escorting,
                  prostitution, solicitation of commercial sexual activity,
                  sugar arrangements involving compensation, and the promotion
                  or sale of sexual content or subscription services are
                  prohibited, whether the transaction is proposed to occur on or
                  off the Service.
                </ListItem>
                <ListItem>
                  <strong>No commercial use.</strong> Advertising, promotion,
                  recruitment, multi-level marketing, and redirecting users to
                  external platforms, paid content, or subscription pages are
                  prohibited unless expressly authorized by NudR in writing.
                </ListItem>
                <ListItem>
                  <strong>No fake or automated accounts.</strong> Each user may
                  operate one account, personally. Bots, scripts, purchased
                  accounts, impersonation, and ban evasion (Section 12.5) are
                  prohibited.
                </ListItem>
                <ListItem>
                  <strong>No illegal activity.</strong> You may not use the
                  Service to arrange, promote, facilitate, or conceal any
                  unlawful activity, including the sale or distribution of
                  controlled substances or weapons, human trafficking, sex
                  trafficking, or money laundering.
                </ListItem>
              </List>
            </Section>

            {/* 11. Enforcement */}
            <Section id="enforcement">
              <SectionTitle>
                <span>12</span>Moderation and Enforcement
              </SectionTitle>

              <SubTitle>12.1 Moderation; No Duty to Monitor</SubTitle>
              <P>
                NudR operates user reporting mechanisms and human review of
                reported content, including profile photographs and Shared
                Media. NudR may, but — except where required by applicable law
                — is under no obligation to, monitor, screen, or review any
                content or conduct on the Service. The existence of moderation
                systems does not constitute a guarantee that violating content
                will be detected, prevented, or removed, and does not create any
                duty owed to any user. NudR's failure to take Enforcement Action
                against a particular violation is not a waiver of its right to
                act on that violation or any other.
              </P>

              <SubTitle>12.2 Reserved Rights</SubTitle>
              <P>
                NudR reserves the right, exercisable at any time and in its sole
                discretion, to remove, blur, restrict the visibility of, or
                refuse to display any content; to restrict, suspend, or
                terminate any account; and to take any other Enforcement Action
                it deems appropriate to protect the Service or its users, with
                or without prior notice, consistent with the Terms and
                applicable law.
              </P>

              <SubTitle>12.3 Enforcement Ladder</SubTitle>
              <P>
                Except for the categories in Section 12.4, Enforcement Actions
                generally follow an escalating ladder, applied in NudR's
                discretion based on the severity, context, and history of the
                violation:
              </P>
              <List>
                <ListItem>
                  <strong>Warning</strong> — for minor, first-time violations.
                </ListItem>
                <ListItem>
                  <strong>Content removal and feature restriction</strong> —
                  removal of violating content and temporary loss of related
                  features (for example, media sharing or discovery visibility).
                </ListItem>
                <ListItem>
                  <strong>Temporary suspension</strong> — account access
                  suspended for a defined period.
                </ListItem>
                <ListItem>
                  <strong>Permanent termination</strong> — account terminated
                  without refund of any prepaid amounts (to the extent permitted
                  by applicable law), with associated identity, device, and
                  payment signals blocked from re-registration.
                </ListItem>
              </List>
              <P>
                NudR may enter the ladder at any step, skip steps, or combine
                measures, based on severity. Nothing in this Section limits
                NudR's rights under Section 12.2 or the Terms.
              </P>

              <SubTitle>12.4 Immediate Permanent Termination</SubTitle>
              <P>
                The following categories bypass the enforcement ladder and
                result in immediate permanent termination, evidence preservation
                where appropriate, and — where required or permitted by law —
                referral to law enforcement:
              </P>
              <List>
                <ListItem>
                  Any content or conduct involving Minors described in Section
                  6.
                </ListItem>
                <ListItem>
                  Sexual violence, sexual exploitation, or the aiding, abetting,
                  or facilitation of either, under Section 9.
                </ListItem>
                <ListItem>
                  Non-consensual intimate content, including sextortion, under
                  Section 10.1.
                </ListItem>
                <ListItem>
                  Romance scams, financial fraud, and commercial sexual services
                  under Section 11.
                </ListItem>
                <ListItem>
                  Credible threats of violence under Section 8.
                </ListItem>
                <ListItem>
                  Human trafficking or sex trafficking, or any facilitation of
                  either.
                </ListItem>
              </List>

              <SubTitle>12.5 Ban Evasion</SubTitle>
              <P>
                Creating or using an account to evade a suspension or
                termination — including through new credentials, devices,
                identities, or the accounts of others — is prohibited. Evading
                accounts may be terminated immediately without notice, and
                evasion may extend or make permanent the underlying Enforcement
                Action.
              </P>

              <SubTitle>12.6 Cooperation with Authorities</SubTitle>
              <P>
                NudR may preserve and disclose content, account information, and
                Enforcement Action records where it reasonably believes
                disclosure is necessary to comply with law, legal process, or
                governmental request; to enforce the Terms or these Guidelines;
                or to protect the rights, property, or safety of NudR, its
                users, or the public — in each case as described in the Privacy
                Policy. Enforcement and safety records may be retained after
                account deletion as described in the Privacy Policy.
              </P>
            </Section>

            {/* 12. Appeals */}
            <Section id="appeals">
              <SectionTitle>
                <span>13</span>Reporting and Appeals
              </SectionTitle>

              <SubTitle>13.1 Reporting</SubTitle>
              <P>
                You may report a profile, a photograph, or a specific message
                directly in the app. Submitting a report authorizes NudR to
                review the reported content — including, for message reports,
                the decrypted content of the reported message and reasonable
                surrounding context — as described in the Terms. You may also
                unmatch or block any user at any time; neither is required
                before, or instead of, reporting. NudR's safety team is staffed
                24 hours a day, 7 days a week. Reports are triaged on arrival
                and prioritized by severity, and NudR works to the response
                targets published on its Reporting &amp; Blocking page. NudR
                does not guarantee any particular outcome. Knowingly
                false, malicious, or retaliatory reporting is itself a violation
                of these Guidelines.
              </P>

              <SubTitle>13.2 Appeals</SubTitle>
              <P>
                If you believe an Enforcement Action against your account was
                made in error, you may submit one appeal per Enforcement Action
                by contacting{" "}
                <a href="mailto:security@nudr.app" style={{ color: "#7c3aed" }}>
                  security@nudr.app
                </a>{" "}
                from the email address associated with your account within
                thirty (30) days of the action, including a description of the
                action and the basis for your appeal. NudR acknowledges appeals
                within two (2) business days and aims to reach a decision within
                ten (10) business days. NudR will review appeals in good faith;
                appeal decisions are made in NudR's sole discretion and are
                final.{" "}
                <strong>
                  No appeal is available for Enforcement Actions under Section 6
                  (content involving Minors).
                </strong>{" "}
                Nothing in this Section limits any non-waivable right you may
                have under applicable law.
              </P>
            </Section>

            {/* 13. General */}
            <Section id="general">
              <SectionTitle>
                <span>14</span>General Provisions
              </SectionTitle>

              <SubTitle>14.1 Changes</SubTitle>
              <P>
                NudR may revise these Guidelines from time to time. If a change
                is material, NudR will provide notice through the Service, by
                email, or by another reasonable method before the change takes
                effect, as required by applicable law. Continued use of the
                Service after a revised version takes effect constitutes
                acceptance of the revision.
              </P>

              <SubTitle>14.2 Severability</SubTitle>
              <P>
                If any provision of these Guidelines is held unenforceable or
                invalid, that provision will be limited or eliminated to the
                minimum extent necessary, and the remaining provisions will
                remain in full force and effect.
              </P>

              <SubTitle>14.3 No Third-Party Beneficiaries</SubTitle>
              <P>
                These Guidelines do not confer any rights or remedies on any
                person other than you and NudR.
              </P>

              <SubTitle>14.4 Relationship to the Terms; Survival</SubTitle>
              <P>
                These Guidelines are governed by the dispute-resolution,
                governing-law, limitation-of-liability, and other general
                provisions of the Terms. Provisions of these Guidelines that by
                their nature should survive termination of your account —
                including Sections 6, 9, 10, 12.5, 12.6, and 14 — survive.
              </P>

              <SubTitle>14.5 Language</SubTitle>
              <P>
                These Guidelines are drafted in English. If NudR provides a
                translation for convenience, the English-language version
                controls in the event of any conflict, to the extent permitted
                by applicable law.
              </P>
            </Section>

            {/* 14. Contact */}
            <Section id="contact">
              <SectionTitle>
                <span>15</span>Contact
              </SectionTitle>
              <ContactList>
                <ContactRow>
                  <b>Trust & Safety:</b>{" "}
                  <a href="mailto:security@nudr.app">security@nudr.app</a>
                </ContactRow>
                <ContactRow>
                  <b>General / Legal:</b>{" "}
                  <a href="mailto:legal@nudr.app">legal@nudr.app</a>
                </ContactRow>
                <ContactRow>
                  <b>Registered Address:</b> 1105 N Buena Vista Street, Burbank,
                  CA 91505
                </ContactRow>
              </ContactList>

              <Callout>
                <P>
                  <strong>
                    If you or someone else is in immediate danger, contact your
                    local emergency services first
                  </strong>
                  , then report the user to us.
                </P>
              </Callout>

              <P style={{ marginTop: "1.4rem", fontSize: "0.85rem" }}>
                © {new Date().getFullYear()} NudR. All rights reserved.
              </P>

              <BackHome to="/">← Back to home</BackHome>
            </Section>
          </Content>
        </Layout>
      </Container>
    </Page>
  );
}
